import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components' // Import styled-components
import { useTheme, alpha } from '@material-ui/core/styles' // Import alpha helper for opacity
import { ButtonGroup } from '@material-ui/core'

// Define base styles for ButtonGroup using styled-components
const BaseButtonGroup = styled(ButtonGroup)`
  box-shadow: none;
  text-transform: none;
  font-family: 'Inter';
  border-radius: 4px; // Add your border-radius or use a function for it
  &:hover {
    box-shadow: none;
  }
`

// Define variant-specific styles for contained ButtonGroup
const ContainedButtonGroup = styled(BaseButtonGroup)`
  .MuiButtonGroup-grouped {
    color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabled
        : theme.palette[color].contrastText};
    background-color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : theme.palette[color].main};
    border-color: ${({ theme, color }) => theme.palette[color].dark} !important;
    &:hover {
      background-color: ${({ theme, color }) => theme.palette[color].dark};
    }
  }
`

// Define variant-specific styles for outlined ButtonGroup
const OutlinedButtonGroup = styled(BaseButtonGroup)`
  .MuiButtonGroup-grouped {
    color: ${({ theme, color, disabled }) =>
      disabled ? theme.palette.action.disabled : theme.palette[color].main};
    border-color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : alpha(theme.palette[color].main, 0.48)} !important;
    &:hover {
      border-color: ${({ theme, color }) =>
        theme.palette[color].main} !important;
      background-color: ${({ theme, color }) =>
        alpha(theme.palette[color].main, theme.palette.action.hoverOpacity)};
    }
  }
`

// Define variant-specific styles for text ButtonGroup
const TextButtonGroup = styled(BaseButtonGroup)`
  .MuiButtonGroup-grouped {
    color: ${({ theme, color, disabled }) =>
      disabled ? theme.palette.action.disabled : theme.palette[color].main};
    &:hover {
      background-color: ${({ theme, color }) =>
        alpha(theme.palette[color].main, theme.palette.action.hoverOpacity)};
    }
  }
`

// CoreButtonGroup component
const CoreButtonGroup = ({
  color = 'primary',
  variant = 'outlined',
  disabled = false,
  children,
  ...props
}) => {
  const theme = useTheme() // Access the theme using useTheme hook

  // Select the button group variant based on the `variant` prop
  const ButtonGroupVariant =
    {
      contained: ContainedButtonGroup,
      outlined: OutlinedButtonGroup,
      text: TextButtonGroup,
    }[variant] || OutlinedButtonGroup

  return (
    <ButtonGroupVariant
      theme={theme} // Pass the theme to the styled component
      color={color} // Pass the color to the styled component
      disabled={disabled} // Pass disabled to styled component
      {...props}
    >
      {children}
    </ButtonGroupVariant>
  )
}

// Prop validation
CoreButtonGroup.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf([
    'inherit',
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
  ]),
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  disabled: PropTypes.bool,
}

export default CoreButtonGroup
